'use client';

import { setCookie } from 'cookies-next';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
  applyCoupon,
  removeCoupon,
  syncLocalCartToBackend,
} from '@/core/lib/api/main/cart';
import { getCart } from '@/core/lib/api/main/cart';
// Assuming you have a getCart API function; add it if not exists
import { MainShopProduct } from '@/core/models/shop-product-model';
import { ShippingFormData } from '@/core/schemas/shippingSchema';
import type { Cart, CartItem, CartPayload } from '@/core/types/cart';

export interface CouponDetails {
  code: string;
  description: string;
  discount_type: string;
  discount_value: number;
  min_purchase_amount: number;
  max_discount_amount: number;
  applied_discount_amount: number;
}

export interface FinancialSummary {
  total_items_count: number;
  products_discount: number;
  coupon_discount: number;
  tax: number;
  payable_price: number;
  total_price_withDiscount: number;
  total_price_withoutDiscount: number;
}

export interface ShippingProfileState {
  status: 'error' | 'success';
  message?: string;
  profile_link?: string;
  data: ShippingFormData | string;
}

export type CartWithCoupon = Cart & {
  coupon_details?: CouponDetails | null;
  financial_summary?: FinancialSummary | null;
};

export interface CouponResponse {
  status: 'success' | 'error';
  message: string;
  data: {
    financial_summary: FinancialSummary;
    coupon_details?: CouponDetails;
  };
}

interface CartStore {
  cart: CartWithCoupon;
  getCart: () => CartWithCoupon;
  addToCart: (
    productInfo: MainShopProduct,
    quantity: number,
    isAuthenticated: boolean
  ) => Promise<void>;
  updateCartItem: (
    productId: number,
    quantity: number,
    isAuthenticated: boolean
  ) => Promise<void>;
  removeFromCart: (
    productId: number,
    isAuthenticated: boolean
  ) => Promise<void>;
  syncCart: (isAuthenticated: boolean) => Promise<void>;

  clearCart: () => void;

  // New: Fetch and sync cart from backend (e.g., on login)
  fetchCartFromBackend: () => Promise<void>;

  // Coupon
  addCouponToCart: (data: {
    coupon_details: CouponDetails | null;
    financial_summary: FinancialSummary | null;
  }) => void;
  removeCouponFromCart: (data: {
    coupon_details: CouponDetails | null;
    financial_summary: FinancialSummary | null;
  }) => void;
  applyCoupon: (code: string) => Promise<CouponResponse | void>;
  removeCoupon: () => Promise<CouponResponse | void>;

  // Shipping states
  isSubmittingShipping: boolean;
  setIsSubmittingShipping: (v: boolean) => void;
  shippingProfile: ShippingProfileState | null;
  setShippingProfile: (profile: ShippingProfileState | null) => void;

  // Global loading
  isCartLoading: boolean;
  setIsCartLoading: (v: boolean) => void;

  // Payment Method
  paymentMethod: 'online' | 'offline';
  setPaymentMethod: (method: 'online' | 'offline') => void;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      cart: {
        uuid: uuidv4(),
        items: [],
      },

      getCart: () => get().cart,

      // Global loading
      isCartLoading: false,
      setIsCartLoading: v => set({ isCartLoading: v }),

      // New: Fetch cart from backend and update local state + storage
      fetchCartFromBackend: async () => {
        const setLoading = get().setIsCartLoading;
        try {
          setLoading(true);

          // Call your getCart API (assuming it returns the structure you provided)
          const response = await getCart();

          // Implement this API function to match the response structure
          console.log('response fetch data', response);

          if (response.status === 'success') {
            if (response.data.items.length === 0 && !response.data.uuid) {
              return;
            }
            const backendData = response.data;
            // Map items to CartItem format
            const updatedItems: CartItem[] = backendData.items.map(
              (item: any) => ({
                product_id: item.product_id,
                quantity: item.quantity,
                product_info: item.product as MainShopProduct, // Assuming product matches MainShopProduct
              })
            );

            // Prepare shipping data (map to ShippingFormData)
            const shippingData: ShippingFormData = {
              recipient_first_name: backendData.recipient_first_name,
              recipient_last_name: backendData.recipient_last_name,
              recipient_mobile: backendData.recipient_mobile,
              address: backendData.address,
              address_plaque: backendData.address_plaque,
              address_floor: backendData.address_floor,
              address_unit: backendData.address_unit,
              postal_code: backendData.postal_code,
              city_id: backendData.city_id,
              location_lat: backendData.location_lat,
              location_lon: backendData.location_lon,
            };

            // Set shipping profile (assuming success state)
            const shippingProfile: ShippingProfileState = {
              status: 'success',
              message: response.message,
              data: shippingData,
            };

            // Update cart with backend data
            const updatedCart: CartWithCoupon = {
              uuid: backendData.uuid,
              items: updatedItems,
              // Note: coupon_details and financial_summary are not in the provided response;
              // If they come from a separate API or need calculation, handle here or fetch separately
              coupon_details: null, // Or fetch if coupon_id is present
              financial_summary: null, // Or calculate based on items
            };

            if (backendData.uuid) {
              setCookie('cart-uuid', backendData.uuid, {
                path: '/', // در کل سایت قابل دسترسی
                maxAge: 60 * 60 * 24 * 365, // ماندگاری یک‌سال
              });
            }

            set({
              cart: updatedCart,
              shippingProfile,
            });

            // Persist to localStorage via Zustand's persist middleware (already handled)
            console.log(
              'Cart synced from backend to local storage',
              response.data
            );
          } else {
            console.error('Failed to fetch cart:', response.message);
          }
        } catch (error) {
          console.error('Error fetching cart from backend:', error);
        } finally {
          setLoading(false);
        }
      },

      addToCart: async (productInfo, quantity, isAuthenticated) => {
        const setLoading = get().setIsCartLoading;
        try {
          setLoading(true);

          const currentCart = get().cart;
          const existingItemIndex = currentCart.items.findIndex(
            //@ts-ignore
            item => item.product_id === productInfo.id
          );

          let updatedItems: CartItem[];
          if (existingItemIndex >= 0) {
            updatedItems = currentCart.items.map((item, index) =>
              index === existingItemIndex
                ? { ...item, quantity: item.quantity + quantity }
                : item
            );
          } else {
            const newItem: CartItem = {
              //@ts-ignore
              product_id: productInfo.id,
              quantity,
              product_info: productInfo,
            };
            updatedItems = [...currentCart.items, newItem];
          }

          const updatedCart = { ...currentCart, items: updatedItems };
          set({ cart: updatedCart });

          if (isAuthenticated) {
            const payload: CartPayload = {
              uuid: currentCart.uuid,
              items: updatedItems.map(item => ({
                product_id: item.product_id,
                quantity: item.quantity,
              })),
            };
            await syncLocalCartToBackend(payload);
            get().removeCouponFromCart({
              coupon_details: null,
              financial_summary: null,
            });
          }
        } catch (error) {
          console.log(error);

          // Error handling
        } finally {
          setLoading(false);
        }
      },

      updateCartItem: async (productId, quantity, isAuthenticated) => {
        const setLoading = get().setIsCartLoading;
        try {
          setLoading(true);

          const currentCart = get().cart;
          if (quantity <= 0) {
            await get().removeFromCart(productId, isAuthenticated);
            return;
          }

          const updatedItems = currentCart.items.map(item =>
            item.product_id === productId ? { ...item, quantity } : item
          );
          const updatedCart = { ...currentCart, items: updatedItems };
          set({ cart: updatedCart });

          if (isAuthenticated) {
            const payload: CartPayload = {
              uuid: currentCart.uuid,
              items: updatedItems.map(item => ({
                product_id: item.product_id,
                quantity: item.quantity,
              })),
            };

            await syncLocalCartToBackend(payload);
            console.log('سبد خرید بعد از به‌روزرسانی سینک شد');
            get().removeCouponFromCart({
              coupon_details: null,
              financial_summary: null,
            });
          }
        } catch (error) {
          console.error('خطا در به‌روزرسانی سبد:', error);
        } finally {
          setLoading(false);
        }
      },

      removeFromCart: async (productId, isAuthenticated) => {
        const setLoading = get().setIsCartLoading;
        try {
          setLoading(true);

          const currentCart = get().cart;
          const updatedItems = currentCart.items.filter(
            item => item.product_id !== productId
          );
          const updatedCart = { ...currentCart, items: updatedItems };
          set({ cart: updatedCart });

          if (isAuthenticated) {
            const payload: CartPayload = {
              uuid: currentCart.uuid,
              items: updatedItems.map(item => ({
                product_id: item.product_id,
                quantity: item.quantity,
              })),
            };
            await syncLocalCartToBackend(payload);
            console.log('سبد خرید بعد از حذف سینک شد');
            get().removeCouponFromCart({
              coupon_details: null,
              financial_summary: null,
            });
          }
        } catch (error) {
          console.error('خطا در حذف از سبد:', error);
        } finally {
          setLoading(false);
        }
      },

      syncCart: async isAuthenticated => {
        const setLoading = get().setIsCartLoading;
        try {
          if (!isAuthenticated) return;
          setLoading(true);

          const currentCart = get().cart;
          if (currentCart.items.length === 0) return;

          const payload: CartPayload = {
            uuid: currentCart.uuid,
            items: currentCart.items.map(item => ({
              product_id: item.product_id,
              quantity: item.quantity,
            })),
          };
          await syncLocalCartToBackend(payload);
          console.log('سبد خرید محلی با بک‌اند سینک شد');
        } catch (error) {
          console.error('خطا در سینک سبد خرید:', error);
        } finally {
          setLoading(false);
        }
      },

      clearCart: () => {
        const newCart = { uuid: uuidv4(), items: [] };
        set({
          cart: newCart,
          shippingProfile: null,
          isSubmittingShipping: false,
        });
        localStorage.removeItem('cart-storage');
        window.dispatchEvent(new Event('storage')); // 👈 درصورت داشتن لیسنر در تب‌های دیگر
      },
      addCouponToCart: data => {
        const currentCart = get().cart;
        set({
          cart: {
            ...currentCart,
            coupon_details: data.coupon_details ?? null,
            financial_summary: data.financial_summary ?? null,
          },
        });
      },

      removeCouponFromCart: data => {
        const currentCart = get().cart;
        set({
          cart: {
            ...currentCart,
            coupon_details: data.coupon_details ?? null,
            financial_summary: data.financial_summary ?? null,
          },
        });
      },

      applyCoupon: async code => {
        const setLoading = get().setIsCartLoading;
        try {
          if (!get().cart?.uuid) return;
          setLoading(true);

          const result = await applyCoupon(get().cart.uuid, code);
          get().addCouponToCart(result.data);
          return result;
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      },

      removeCoupon: async () => {
        const setLoading = get().setIsCartLoading;
        try {
          const cart = get().cart;
          if (!cart?.uuid || !cart.coupon_details) return;
          setLoading(true);

          const result = await removeCoupon(cart.uuid);
          get().removeCouponFromCart(result.data);
          return result;
        } catch (error) {
          console.error('خطا در حذف کوپن:', error);
        } finally {
          setLoading(false);
        }
      },

      // Shipping
      isSubmittingShipping: false,
      setIsSubmittingShipping: v => set({ isSubmittingShipping: v }),
      shippingProfile: null,
      setShippingProfile: profile => set({ shippingProfile: profile }),
      paymentMethod: 'online', // Default value
      setPaymentMethod: method => set({ paymentMethod: method }),
    }),
    {
      name: 'cart-storage',
      partialize: state => ({
        cart: state.cart,
        paymentMethod: state.paymentMethod,
        // Note: shippingProfile is not persisted by default; add if needed
      }),
    }
  )
);
